import { useState } from 'react'
import { Layers } from 'lucide-react'
import { Ago } from '@/components/Ago'
import { Facts } from '@/components/Facts'
import { PoolBars } from '@/components/PoolBar'
import { ToneBadge } from '@/components/ToneBadge'
import { settingsSourceWord } from '@/domain/cluster'
import { DASH, exact } from '@/ui/format'
import { clusterIconTone, clusterStatus, clusterTone } from '../model'
import type { ClusterPlacement } from '../model'
import { ClusterMenu } from './ClusterMenu'
import { PlacedNodeCard } from './PlacedNodeCard'

// 클러스터 카드. 머리(이름, 정상이 아닐 때만 상태 배지, 오른쪽의 메뉴, 그 아래 2×2 자원 막대 넷) → 본문(회색 바탕 위에 노드 카드들이 격자로).
// 관리자가 여기서 보는 것은 셋이다 — 어떤 노드가 묶여 있는가, 얼마나 쓰는가, 고장난 것은 없는가. 클러스터는 이름 위·막대 아래의 2×2, 노드는 이름 옆에 막대가 붙은 한 줄 넷이라 묶음과 낱개가 꼴로 갈린다.
// 클러스터의 상세 화면은 없다: 조작은 메뉴에, 상세 값은 고급 모드의 이름·값 목록에 있다.
// 노드 카드 최소 200px: 카드 폭 456px(안쪽 432px)에서 둘, 1280px 의 두 열(스크롤바를 빼면 448px)에서도 둘이라 열 수가 튀지 않는다.

// 아이콘의 색은 노드 카드의 것과 같은 넷이다 — 정상 초록 · 일부 비정상 노랑 · 답 없음 빨강 · 준비 파랑(`clusterIconTone`).
const ICON = { ok: 'text-ok', warn: 'text-warn', danger: 'text-danger', preparing: 'text-brand' } as const

export function ClusterCard({ placed }: { placed: ClusterPlacement }) {
  const { cluster: c, resources: r } = placed
  const [renamedTo, setRenamedTo] = useState<string | null>(null)
  const [removed, setRemoved] = useState(false)
  // 제거된 클러스터는 목록이 다시 읽히면 빠진다. 그 사이에도 카드는 남지 않는다.
  if (removed) return null
  const name = renamedTo ?? c.name
  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      <div className="border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Layers className={`size-4 flex-none ${ICON[clusterIconTone(c, placed.nodes.map((p) => p.node.row.status))]}`} />
          <span className="truncate text-[15px] font-semibold">{name}</span>
          {clusterTone(c) === 'ok' ? null : <ToneBadge tone={clusterTone(c)}>{clusterStatus(c)}</ToneBadge>}
          <div className="ml-auto">
            <ClusterMenu cluster={c} name={name} onRenamed={setRenamedTo} onRemoved={() => setRemoved(true)} />
          </div>
        </div>
        <div className="mt-3">
          <PoolBars cpu={r.cpu} memory={r.memory} storage={r.storage} gpu={r.gpu} />
        </div>
        {/* 고급의 상세 값 — 한 줄에 이름 하나와 값 하나. 일반에서는 아무것도 서지 않는다. */}
        <div className="mt-3 empty:hidden">
          <Facts
            rows={[]}
            expert={[
              ['가상화 이름', c.pve_cluster_name ?? DASH],
              ['노드', `${c.member_count}대`],
              ['응답', `${c.live_count}/${c.member_count}대`],
              ['마지막 확인', exact(c.last_member_seen_at)],
              ['임대 스토리지', c.lease_storage ?? DASH],
              ['브리지', c.lease_bridge ?? DASH],
              ['풀', c.lease_pool ?? DASH],
              ['설정', <>{settingsSourceWord(c.lease_settings_source)} · <Ago at={c.lease_settings_checked_at} /> 확인</>],
            ]}
          />
        </div>
      </div>
      <div className="bg-surface-2 p-3">
        {placed.nodes.length === 0 ? (
          <div className="py-4 text-center text-[12px] text-muted-foreground">노드 없음</div>
        ) : (
          <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(min(200px,100%),1fr))]">
            {placed.nodes.map((p) => (
              <PlacedNodeCard key={p.node.row.node.id} placed={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
